import _      from 'lodash'
import moment from 'moment'

import JSONStorage                 from 'alto-utils/lib/json_storage'
import Backend, { ForbiddenError } from 'alto-utils/lib/backend'

import { User } from './models'

class AuthStorage
  # override this
  @fields:
    [{ name: 'token',       required: true  },
     { name: 'permissions', required: false }
     { name: 'user',        required: true, \
       serializer: User.serialize.bind(User), deserializer: User.deserialize.bind(User) }]

  constructor: ->
    @storage = new JSONStorage(localStorage ? {})
    @constructor.fields.forEach (field) =>
      Object.defineProperty(@, field.name, get: -> @load()[field.name])

  load: ->
    if @storage.isFilled() then @_load() else @_defaultValue()

  save: (authInfo) ->
    @_validate(authInfo)
    @_save(authInfo)

  update: (authInfo) ->
    @save(_.assign(@load(), authInfo))

  setHasPassword: (hasPassword) ->
    authInfo = @_load()
    authInfo.user.hasPassword = hasPassword

    @_save(authInfo)

  clear:    -> @storage.clear()
  isFilled: -> @storage.isFilled()
  isEmpty:  -> @storage.isEmpty()

  _save: (authInfo) -> @storage.write(@_serialize(authInfo))
  _load:            -> @_deserialize(@storage.read())

  _validate: (authInfo) ->
    isValid = _.every(@constructor.fields, (field) -> not field.required or authInfo[field.name]?)
    throw new TypeError('invalid auth info', authInfo) unless isValid

  _serialize: (authInfo) ->
    pairs = @constructor.fields.map (field) ->
      value      = authInfo[field.name] ? field.default ? null
      serializer = field.serializer     ? _.identity

      [field.name, value and serializer(value)]

    _.fromPairs(pairs)

  _deserialize: (data) ->
    pairs = @constructor.fields.map (field) ->
      value        = data[field.name]
      deserializer = field.deserializer ? _.identity

      [field.name, value and deserializer(value)]

    _.fromPairs(pairs)

  _defaultValue: ->
    _.fromPairs(@constructor.fields.map (field) -> [field.name, field.default ? null])

class AuthBackend
  constructor: (@backendUrl, @frontendUrl, @storage) ->
    @backend = new Backend(@backendUrl, null)

  getToken: (username, password) ->
    @backend.request('POST', '/auth/get_token', body: { username, password })
      .then(@getTokenHandler)
      .catch (error) ->
        if error.name == 'ForbiddenError'
          throw error
        else
          console.error('received unknown auth error response', error)
          throw error

  getTokenHandler: ({ token, user, permissions = [] }) ->
    { token, permissions, user: User.deserialize(user) }

  testToken: ->
    if @storage.isFilled()
      @backend.requestWithToken(@storage.token, 'GET', '/auth/test_token')
    else
      Promise.reject(new ForbiddenError())

  register: ({ email, username, password, useEmailAsUsername = false }) ->
    @backend.request('POST', '/auth/register',
                    qs:   { redirect: @frontendUrl },
                    body: { use_email_as_username: useEmailAsUsername, email, username, password })

  resendActivationEmail: (email) ->
    @backend.request('POST', '/auth/resend_activation_email',
                    qs: { email: email, redirect: @frontendUrl })

  initPasswordReset: (email) ->
    @backend.request('POST', '/auth/init_password_reset',
                    qs: { email: email, redirect: @frontendUrl + '/auth/confirm_password_reset' })

  confirmPasswordReset: (userId, token, password) ->
    @backend.request('POST', '/auth/reset_password',
                    qs:   { user_id: userId, token }
                    body: { password })

  changePassword: (currentPassword, newPassword) ->
    @backend.requestWithToken(@storage.token, 'POST', '/auth/change_password',
                              body: { current_password: currentPassword, new_password: newPassword })
      .then (response) => @storage.setHasPassword(true)

  deleteUser: ->
    @backend.requestWithToken(@storage.token, 'DELETE', '/auth/delete_user')

class Auth
  @storageClass: AuthStorage
  @backendClass: AuthBackend

  constructor: (@backendUrl, @frontendUrl) ->
    @storage  = new @constructor.storageClass
    @backend  = new @constructor.backendClass(@backendUrl, @frontendUrl, @storage)
    @timer    = null

  logIn: (username, password) ->
    @backend.getToken(username, password).then (response) =>
      { token, user, permissions } = response

      @storage.save({ token, user, permissions })

  logOut: ->
    @storage.clear()

  testToken:             (args...) -> @backend.testToken(args...)
  register:              (args...) -> @backend.register(args...)
  changePassword:        (args...) -> @backend.changePassword(args...)
  resendActivationEmail: (args...) -> @backend.resendActivationEmail(args...)
  initPasswordReset:     (args...) -> @backend.initPasswordReset(args...)
  confirmPasswordReset:  (args...) -> @backend.confirmPasswordReset(args...)
  deleteUser:            (args...) -> @backend.deleteUser(args...)

  Object.defineProperty(@::, 'isLoggedIn',   get: -> @storage.isFilled())
  Object.defineProperty(@::, 'authInfo',     get: -> @storage.load())
  Object.defineProperty(@::, 'token',        get: -> @authInfo.token)
  Object.defineProperty(@::, 'loggedInUser', get: -> @authInfo.user)
  Object.defineProperty(@::, 'permissions',  get: -> @authInfo.permissions)

  havePermission: (name) ->
    @loggedInUser.isStaffOrAdmin or
      _.find(@authInfo.permissions, (perm) -> _.split(perm, '.')[1] == name)?

Auth.Storage = AuthStorage
Auth.Backend = AuthBackend

export default Auth
